/**
 * The email client's Settings rows (piece 32; decisions 0790 to 0796): nine
 * `SettingValue` keys, each edited in Settings › Email and none of them a table
 * of the piece. The legal footer is seeded by piece 15 beside its own rows.
 */

/** 🔒 How often a host with no IMAP IDLE is polled, in seconds (decision 0796). */
export const MAIL_SYNC_POLL_SECONDS_KEY = 'mail.syncPollSeconds';
export const MAIL_SYNC_POLL_SECONDS_DEFAULT = 60;
/** Person scope over an Organization default of zero, which is immediate. */
export const MAIL_UNDO_SEND_DELAY_KEY = 'mail.undoSendDelaySeconds';
/** 🔒 The floor a departed mailbox stands before a purge is permitted, a super admin's alone (decision 0796). */
export const MAIL_DEPARTED_RETENTION_DAYS_KEY = 'mail.departedMailboxRetentionDays';
export const MAIL_DEPARTED_RETENTION_DAYS_DEFAULT = 365;
export const MAIL_CERTIFICATE_AUTO_DISCOVERY_KEY = 'mail.certificateAutoDiscovery';
/** Person scope over an Organization default of off. */
export const MAIL_CLIENT_ACCESS_ENABLED_KEY = 'mail.clientAccessEnabled';
/** 🔒 Empty keeps mail for ever (decision 0795). */
export const MAIL_RETENTION_DAYS_KEY = 'mail.retentionDays';
/** 🔒 A map from a permission Module to the Mailbox its mail goes from; empty sends from the person (decision 0791). */
export const MAIL_MODULE_SENDERS_KEY = 'mail.moduleSenders';
/** 🔒 Person scope over an Organization default of off: out-of-office from approved leave is opt-in (decision 0792). */
export const MAIL_OUT_OF_OFFICE_FROM_LEAVE_KEY = 'mail.outOfOfficeFromLeave';

/**
 * 🔒 The one daily job of the three this piece runs (piece 32, Mailbox access): the open-tracking detail purge, on the
 * job clock under its own hour key. `syncMailboxes` and `releaseHeldMail` run on the client's own minute timer instead,
 * because a mailbox polled once a night is not a mailbox (decision 0809).
 */
export const PURGE_OPEN_TRACKING_JOB = 'purgeOpenTrackingDetail';
export const PURGE_OPEN_TRACKING_HOUR_KEY = 'mail.purgeOpenTrackingRunHour';
export const PURGE_OPEN_TRACKING_DEFAULT_HOUR = 3;
/** 🔒 An open's user agent and address are kept this many days after the open, and the open row for good (decision 0162). */
export const OPEN_TRACKING_DETAIL_DAYS = 90;
/** The two minute-scale jobs, named the way the job clock names its own, for their run reports. */
export const SYNC_MAILBOXES_JOB = 'syncMailboxes';
export const RELEASE_HELD_MAIL_JOB = 'releaseHeldMail';
/** How often held mail is looked at, in seconds; an undo delay shorter than this waits for the next look. */
export const RELEASE_HELD_MAIL_SECONDS = 15;

/** 🔒 Leave must cover this many consecutive working days before it raises a rule or an offer (decision 0792). */
export const OUT_OF_OFFICE_MINIMUM_WORKING_DAYS = 2;
/** 🔒 A mailbox's quota warns at this share of its limit (decision 0795). */
export const MAILBOX_QUOTA_WARNING_PERCENT = 80;

/** The Organization rows the install seed writes once and never overwrites; a null is a row seeded empty. */
export const PIECE_32_SETTINGS: readonly { key: string; value: number | boolean | Record<string, string> | null }[] = [
  { key: MAIL_SYNC_POLL_SECONDS_KEY, value: MAIL_SYNC_POLL_SECONDS_DEFAULT },
  { key: MAIL_UNDO_SEND_DELAY_KEY, value: 0 },
  { key: MAIL_DEPARTED_RETENTION_DAYS_KEY, value: MAIL_DEPARTED_RETENTION_DAYS_DEFAULT },
  { key: MAIL_CERTIFICATE_AUTO_DISCOVERY_KEY, value: false },
  { key: MAIL_CLIENT_ACCESS_ENABLED_KEY, value: false },
  { key: MAIL_RETENTION_DAYS_KEY, value: null },
  { key: MAIL_MODULE_SENDERS_KEY, value: {} },
  { key: MAIL_OUT_OF_OFFICE_FROM_LEAVE_KEY, value: false },
];

/** The poll interval a stored value asks for, never below ten seconds and the seeded sixty when unreadable. */
export function pollSecondsFrom(stored: unknown): number {
  return typeof stored === 'number' && Number.isFinite(stored) && stored >= 10 ? Math.floor(stored) : MAIL_SYNC_POLL_SECONDS_DEFAULT;
}

/** The mailbox a module's mail goes from: the map's entry, or null for the sending person's own (decision 0791). */
export function moduleSenderMailboxId(stored: unknown, module: string): string | null {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return null;
  const entry = (stored as Record<string, unknown>)[module];
  return typeof entry === 'string' && entry.length > 0 ? entry : null;
}

/** Use against a quota, in whole percent; null where the mailbox has no quota (decision 0795). */
export function quotaPercent(usedBytes: number, quotaMegabytes: number | null): number | null {
  if (quotaMegabytes === null || quotaMegabytes <= 0) return null;
  return Math.floor((usedBytes / (quotaMegabytes * 1024 * 1024)) * 100);
}

/**
 * 🔒 Why a retention value is refused, in a sentence, or null (decisions 0795, 0796, 1237): a whole number of days
 * from one to 36,500. `mail.retentionDays` may be blank, which keeps mail for ever; the departed mailbox's floor may not,
 * because a floor of nothing would permit a purge on the day a person leaves.
 */
export function mailRetentionProblem(key: string, value: unknown): string | null {
  const blank = value === null || value === undefined || value === '';
  if (blank) return key === MAIL_RETENTION_DAYS_KEY ? null : 'The departed mailbox floor needs a number of days; it cannot be left blank.';
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 36_500) return 'A retention period is a whole number of days from 1 to 36,500.';
  return null;
}
